import React, { useState } from 'react';

import { usePostLogout } from 'apis/handler/auth/usePostLogout';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from 'components/ui/dropdown-menu';
import { useModal } from 'context/ModalContext';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useRouteChangeHandler } from 'shared/hooks';
import { useInfoStore } from 'store/useInfoStore';

import { ReactComponent as KakaotalkIcon } from '../../../assets/icons/ic_kakaotalk.svg';
import { ReactComponent as MailIcon } from '../../../assets/icons/ic_mail.svg';
import { ReactComponent as WhatsappIcon } from '../../../assets/icons/ic_whatsapp.svg';
import { HamburgerIcon, HelpIcon } from './Icons';

type HamburgerMenuProps = {
  isLogin: boolean;
};

const HamburgerMenu = ({ isLogin }: HamburgerMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('common');
  const { handleRouteChange } = useRouteChangeHandler();
  const { lang } = useParams<{ lang?: string }>();
  const { resetInfo } = useInfoStore();
  const { openModal, MODAL_TYPES } = useModal();

  const { mutate: logoutMutation } = usePostLogout({
    onSuccess: () => {
      handleRouteChange('/');
      resetInfo();
      setIsOpen(false);
    },

    onError: () => {
      handleRouteChange('/');
      resetInfo();
      setIsOpen(false);
    },
  });

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-[#F6F6F6]"
          aria-label="Menu"
        >
          <HamburgerIcon />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-[220px] rounded-lg bg-white p-0 shadow-[0px_3px_12.1px_0px_rgba(0,0,0,0.14)]">
        <div className="flex flex-col px-2 py-1.5">
          {/* Mobile-only navigation items (데스크톱 GNB hover 가 모바일에서 작동 안 하므로
              sub-menu 도 모두 평탄화하여 노출) */}
          <div className="flex flex-col md:hidden">
            <DropdownMenuItem
              className="text-b6 flex cursor-pointer items-center rounded-md px-2 py-1.5 font-medium hover:bg-[#F8F8F8]"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_menu',
                  menu: 'Room Search',
                  section: 'GNB',
                });
                handleRouteChange('/listing');
                setIsOpen(false);
              }}
            >
              <span>{t('navigation.roomsearch')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-b6 flex cursor-pointer items-center rounded-md px-2 py-1.5 font-medium hover:bg-[#F8F8F8]"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_menu',
                  menu: 'Find your service',
                  section: 'Services',
                });
                handleRouteChange('/all-services');
                setIsOpen(false);
              }}
            >
              <span>{t('navigation.services')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-b6 flex cursor-pointer items-center rounded-md px-2 py-1.5 font-medium hover:bg-[#F8F8F8]"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_menu',
                  menu: 'Checkmate Mall',
                  section: 'GNB',
                });
                handleRouteChange('/checkmate-mall');
                setIsOpen(false);
              }}
            >
              <span>{t('navigation.checkmatemall')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-b6 flex cursor-pointer items-center rounded-md px-2 py-1.5 font-medium hover:bg-[#F8F8F8]"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_menu',
                  menu: 'Booking Guide',
                  section: 'Guide',
                });
                handleRouteChange('/guide');
                setIsOpen(false);
              }}
            >
              <span>{t('navigation.bookingguide')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-b6 flex cursor-pointer items-center rounded-md px-2 py-1.5 font-medium hover:bg-[#F8F8F8]"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_menu',
                  menu: 'Korean Housing Info',
                  section: 'Guide',
                });
                handleRouteChange('/korean-housing-info');
                setIsOpen(false);
              }}
            >
              <span>{t('navigation.koreanhousinginfo')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-b6 flex cursor-pointer items-center rounded-md px-2 py-1.5 font-medium hover:bg-[#F8F8F8]"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_menu',
                  menu: 'Blog',
                  section: 'Guide',
                });
                setIsOpen(false);
                window.location.href = lang ? `/${lang}/blog/` : '/blog/';
              }}
            >
              <span>{t('navigation.blog')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-b6 flex cursor-pointer items-center rounded-md px-2 py-1.5 font-medium hover:bg-[#F8F8F8]"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_menu',
                  menu: 'About Checkmate',
                  section: 'About Us',
                });
                handleRouteChange('/about-us');
                setIsOpen(false);
              }}
            >
              <span>{t('navigation.aboutcheckmate')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-b6 flex cursor-pointer items-center rounded-md px-2 py-1.5 font-medium hover:bg-[#F8F8F8]"
              onClick={() => {
                window.dataLayer?.push({
                  event: 'click_menu',
                  menu: 'Reviews',
                  section: 'About Us',
                });
                handleRouteChange('/reviews');
                setIsOpen(false);
              }}
            >
              <span>{t('navigation.reviews')}</span>
            </DropdownMenuItem>
            {!isLogin && (
              <DropdownMenuItem
                className="text-b6 flex cursor-pointer items-center rounded-md px-2 py-1.5 font-medium text-[#FF7A00] hover:bg-[#F8F8F8]"
                onClick={() => {
                  window.dataLayer?.push({
                    event: 'click_menu',
                    menu: 'login',
                    section: 'Hamburger menu',
                  });
                  openModal(MODAL_TYPES.LOGIN);
                  setIsOpen(false);
                }}
              >
                <span>{t('navigation.login')}</span>
              </DropdownMenuItem>
            )}
            <div className="my-1 h-px bg-[#EAEAEA]" />
          </div>

          {/* Help Center */}
          <DropdownMenuItem
            className="text-b6 flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-[#F8F8F8]"
            onClick={() => {
              window.dataLayer?.push({
                event: 'click_menu',
                menu: 'help',
                section: 'Hamburger menu',
              });
              window.open(
                'https://buttercup-coral-e9a.notion.site/Help-Center-a65dec1c44ca44958a2d192d5119017e?pvs=4',
                '_blank',
              );
            }}
          >
            <HelpIcon />
            <span>{t('support.helpCenter')}</span>
          </DropdownMenuItem>

          <div className="my-1 h-px bg-[#EAEAEA]" />

          {/* Contact options */}
          <DropdownMenuItem
            className="text-b6 flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-[#F8F8F8]"
            onClick={() => {
              window.dataLayer?.push({
                event: 'click_menu',
                menu: 'kakao',
                section: 'Hamburger menu',
              });
              window.open('http://pf.kakao.com/_xjSxcxgxj', '_blank');
            }}
          >
            <KakaotalkIcon className="h-4 w-4" />
            <span>{t('support.kakaoTalk')}</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="text-b6 flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-[#F8F8F8]"
            onClick={() => {
              window.dataLayer?.push({
                event: 'click_menu',
                menu: 'whatsapp',
                section: 'Hamburger menu',
              });
              window.open('https://wa.me/821087033395', '_blank');
            }}
          >
            <WhatsappIcon className="h-4 w-4" />
            <span>{t('support.whatsapp')}</span>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="text-b6 flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-[#F8F8F8]"
            onClick={() => {
              window.dataLayer?.push({
                event: 'click_menu',
                menu: 'mail',
                section: 'Hamburger menu',
              });
              window.location.href = 'mailto:official@checkmatekorea.com';
            }}
          >
            <MailIcon className="h-4 w-4" />
            <span>Email</span>
          </DropdownMenuItem>

          {/* Logout button (login only) */}
          {isLogin && (
            <>
              <div className="my-1 h-px bg-[#EAEAEA]" />
              <DropdownMenuItem
                className="text-b6 cursor-pointer rounded-md px-2 py-1.5 hover:bg-[#F8F8F8]"
                onClick={() => {
                  window.dataLayer?.push({
                    event: 'click_menu',
                    menu: 'logout',
                    section: 'Hamburger menu',
                  });
                  logoutMutation();
                }}
              >
                <span>{t('userMenu.logout')}</span>
              </DropdownMenuItem>
            </>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default HamburgerMenu;
